import { Icon } from '@iconify/react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpClient } from '@/Config/api/httpClient.config';
import { InviteDto } from '@/Domain/api/AI/dto/Invite.dto';
import { Alert, AlertDescription, AlertTitle } from '@/Domain/components/Shared/Alert/Alert';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import CloseIcon from '@/Domain/components/icons/Close.icon';

// TODO: refactor - use tailwind and move functions to hook
export const AiInvitation = () => {
  const navigate = useNavigate();

  const { data: invitesData } = useQuery<InviteDto[]>({
    queryKey: ['my-invites'],
    refetchOnMount: true,
  });

  const [invites, setInvites] = useState<InviteDto[]>([]);

  useEffect(() => {
    if (invitesData != null) {
      setInvites(invitesData);
    }
  }, [invitesData]);

  const handleAcceptInvite = async (invite: InviteDto): Promise<void> => {
    try {
      const response = await httpClient.post(
        `/v1/api/scenario_users/invites/accept/${invite.token}`,
        {},
        {},
      );
      if (response.status === 200) {
        navigate(`/i/${invite.scenario_id}`, { viewTransition: true });
        window.location.reload();
      } else {
        console.error('Failed to accept invite');
      }
    } catch (error) {
      console.error('Error accepting invite:', error);
    }
  };

  const handleDeleteInvite = async (invite: InviteDto): Promise<void> => {
    try {
      const response = await httpClient.delete(`/v1/api/scenario_users/invites/${invite.token}`);
      if (response.status === 200) {
        setInvites((prevInvites) => prevInvites.filter((i) => i.token !== invite.token));
      } else {
        console.error('Failed to delete invite');
      }
    } catch (error) {
      console.error('Error deleting invite:', error);
    }
  };

  return (
    <TooltipProvider>
      {invites.map((invite, index) => (
        <Alert variant="warning" key={index}>
          <AlertTitle>
            <Icon icon="mingcute:invite-line" />
            <span>New invitation</span>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={async () => await handleDeleteInvite(invite)}
                  variant="link"
                  size="icon"
                  className="ml-auto text-orange-600"
                >
                  <CloseIcon />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Decline invite</TooltipContent>
            </Tooltip>
          </AlertTitle>
          <AlertDescription>
            You received an invitation to a new AI Assistant
            {invite.one_word_description !== '' && (
              <i className="ml-1">{invite.one_word_description}</i>
            )}
            .{' '}
            <Button
              variant="link"
              onClick={async () => await handleAcceptInvite(invite)}
              className="p-0 h-auto text-orange-600 font-medium underline"
            >
              Click here to accept it
            </Button>
          </AlertDescription>
        </Alert>
      ))}
    </TooltipProvider>
  );
};
